/**
 * Billing-attention sections (TMMPDM-0011, TMMPDM-0099). The page at `/billing-attention` owns the
 * read, the tabs and Declare products…. TMMPDM-0072 (TMM-B3): nothing the server wrote is shown — a
 * FAILED statement says "The last attempt failed." (its status alone, TMMPDM-0077) and a statement's
 * `lastError` is offered as Copy details for support (the raw text is only ever copied, as on the
 * GHL diagnosis), and a parked event is its event type in words, without `exceptionClass`.
 *
 * TMMPDM-0093 (tmm-api 0159): unhandled payment events, products to review, and tax-classification
 * failures. An empty or absent list is "None." The tab already names the section, so these
 * components render no heading of their own.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, useNotify } from '@/components/page';
import type { ErrorTranslate } from '@/api/errors';
import { Amount } from '@/components/ui';
import type {
  BillingAttention,
  FeedStall,
  SkuReview,
  StatementView,
  TaxClassificationFailure,
  UnhandledFeedEvents,
} from '@/api/tmm/types';
import { formatDate, formatInstant, formatNumber } from '@/format';
import { eventTypeWords } from '@/routes/billing/parkedEventCopy';
import { statementSupportDetails } from './statementSupportDetails';

/** An identifier: the mono face, breaking anywhere so a long id wraps inside its column. */
const monoSx: SxProps<Theme> = (theme) => ({ ...theme.typography.mono, wordBreak: 'break-all' });

/**
 * tmm-api's eight products at the pin (`Skus`). `SkuReview.sku` is a plain string, so a product
 * outside this list is shown as its code (the one thing that identifies it).
 */
const KNOWN_SKUS: ReadonlySet<string> = new Set([
  'tmm.subscription',
  'tmm.guests',
  'tmm.connections',
  'tmm.ai_wallet.credit',
  'tmm.partner.addon.custom_ui',
  'tmm.partner.addon.mobile_app',
  'tmm.partner.minimum_shortfall',
  'tmm.partner.statement_deficit',
]);

/** `tmm.ai_wallet.credit` → `tmm_ai_wallet_credit`: a catalog key segment has no dot. */
function skuKey(sku: string): string {
  return sku.replace(/\./g, '_');
}

function skuWords(sku: string, t: ErrorTranslate): string | null {
  if (!KNOWN_SKUS.has(sku)) return null;
  return t(`ai.attention.sku.${skuKey(sku)}`, sku);
}

/** The charge kinds `TaxClassificationFailure.chargeKind` carries at the pin; others show their code. */
const KNOWN_CHARGE_KINDS: ReadonlySet<string> = new Set([
  'WALLET_CHARGE',
  'ADDON_CHARGE',
  'RESELLER_CHARGE',
]);

function chargeKindWords(kind: string, t: ErrorTranslate): string | null {
  if (!KNOWN_CHARGE_KINDS.has(kind)) return null;
  return t(`ai.attention.chargeKind.${kind}`, kind);
}

/** What a person copies for support: English `key: value` lines whatever the locale (TMM-B3); an unsent value is `-`. */
const supportValue = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '-' : String(v);

function taxFailureSupportDetails(row: TaxClassificationFailure): string {
  return [
    `chargeId: ${row.chargeId}`,
    `chargeKind: ${supportValue(row.chargeKind)}`,
    `tenantId: ${supportValue(row.tenantId)}`,
    `resellerId: ${supportValue(row.resellerId)}`,
    `failedAt: ${supportValue(row.failedAt)}`,
  ].join('\n');
}

function skuReviewSupportDetails(row: SkuReview): string {
  return [
    `sku: ${supportValue(row.sku)}`,
    `since: ${supportValue(row.since)}`,
    `detail: ${supportValue(row.detail)}`,
  ].join('\n');
}

function NoneLine() {
  const { t } = useCopy();
  return <EmptyState surface title={t('ai.attention.none', 'None.')} />;
}

/**
 * TMMAPI-0126: a pay-api feed event a lock keeps giving back. `null` is the server's "not stalled";
 * `seq` and `since` are shown as sent. Present on load, so `role="note"`, not a live alert.
 */
export function FeedStalled({ stall, locale }: { stall: FeedStall | null; locale: string }) {
  const { t } = useCopy();
  if (stall === null) return null;
  return (
    <Alert severity="warning" role="note">
      {t(
        'ai.attention.feedStalled',
        'The payment feed has been stalled since {{since}}. Event {{seq, number}} is blocked by a lock.',
        { since: formatInstant(stall.since, locale), seq: stall.seq },
      )}
    </Alert>
  );
}

export function StatementsNeedAttention({
  rows,
  locale,
}: {
  rows: StatementView[];
  locale: string;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const copyDetails = async (row: StatementView) => {
    try {
      await navigator.clipboard.writeText(statementSupportDetails(row));
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };
  return (
    <Box>
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('ai.attention.statementsAria', 'Statements needing attention')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.period', 'Period')}</TableCell>
                <TableCell>{t('ai.attention.col.status', 'Status')}</TableCell>
                <TableCell align="right">{t('ai.attention.col.total', 'Total')}</TableCell>
                <TableCell>{t('ai.attention.col.attempts', 'Attempts')}</TableCell>
                <TableCell>{t('ai.attention.col.lastError', 'Last error')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {formatDate(row.periodStart, locale)} – {formatDate(row.periodEnd, locale)}
                  </TableCell>
                  <TableCell>
                    {t(`statements.chargeStatus.${row.chargeStatus}`, row.chargeStatus)}
                  </TableCell>
                  <TableCell align="right">
                    <Amount minor={row.totalMinor} currency={row.currency} locale={locale} />
                  </TableCell>
                  <TableCell>{row.attempts}</TableCell>
                  <TableCell>
                    {row.chargeStatus !== 'FAILED' &&
                    (row.lastError === undefined || row.lastError === '') ? (
                      '—'
                    ) : (
                      <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                        {row.chargeStatus === 'FAILED' ? (
                          <Box component="span">
                            {t('ai.attention.lastAttemptFailed', 'The last attempt failed.')}
                          </Box>
                        ) : null}
                        {row.lastError === undefined || row.lastError === '' ? null : (
                          <Button
                            size="small"
                            variant="outlined"
                            aria-label={t(
                              'ai.attention.copyDetailsAria',
                              'Copy details for support for statement {{id, number}}',
                              { id: row.id },
                            )}
                            onClick={() => void copyDetails(row)}
                          >
                            {t('ai.attention.copyDetails', 'Copy details for support')}
                          </Button>
                        )}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export function Divergences({ rows }: { rows: BillingAttention['divergences'] }) {
  const { t } = useCopy();
  return (
    <Box>
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('ai.attention.divergencesAria', 'Reconcile divergences')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.subscriptionId', 'Subscription id')}</TableCell>
                <TableCell>{t('ai.attention.col.payApi', 'pay-api subscription')}</TableCell>
                <TableCell>{t('ai.attention.col.fields', 'Fields')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.subscriptionId}-${row.payApiSubscriptionId ?? ''}`}>
                  <TableCell>{row.subscriptionId}</TableCell>
                  <TableCell>{row.payApiSubscriptionId ?? '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(row.fields).map(([key, value]) => (
                        <Chip key={key} size="small" label={`${key}: ${value}`} />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export function SkippedEvents({
  rows,
  locale,
}: {
  rows: BillingAttention['skippedEvents'];
  locale: string;
}) {
  const { t } = useCopy();
  return (
    <Box>
      {/* TMMPDM-0071: the count is unresolved parked events (tmm-api 0121); it opens the screen. */}
      <Link component={RouterLink} to="/billing/parked-events" underline="hover">
        {t('ai.attention.parked', 'Parked payment events ({{count, number}})', {
          count: rows.length,
        })}
      </Link>
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('ai.attention.parkedAria', 'Parked payment events')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.seq', 'Seq')}</TableCell>
                <TableCell>{t('ai.attention.col.eventType', 'Event type')}</TableCell>
                <TableCell>{t('ai.attention.col.attempts', 'Attempts')}</TableCell>
                <TableCell>{t('ai.attention.col.appliedAt', 'Applied at')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.seq}>
                  <TableCell>{row.seq}</TableCell>
                  <TableCell>
                    {eventTypeWords(row.eventType, t) ?? (
                      <Box component="code" sx={monoSx}>
                        {row.eventType}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{row.attempts}</TableCell>
                  <TableCell>{row.appliedAt ? formatDate(row.appliedAt, locale) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/** Copies `text` for support and says so (or says it could not), as the statement rows do. */
function useCopyDetails() {
  const { t } = useCopy();
  const notify = useNotify();
  return async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notify.success(t('components.copied', 'Copied'));
    } catch {
      notify.info(t('components.copyFailed', "Couldn't copy — select the text instead."));
    }
  };
}

/** A section's one-line note; the tab already names it. Catalog copy, never server text. */
function SectionNote({ note }: { note: string }) {
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {note}
    </Typography>
  );
}

/** An identifier that may be unset: the mono face, or a dash. */
function IdCell({ value }: { value: number | null }) {
  return value === null ? (
    <>—</>
  ) : (
    <Box component="code" sx={monoSx}>
      {value}
    </Box>
  );
}

/** 0159 (MM-D-139): one row per event type kept `UNHANDLED`, with its count and oldest, as sent. */
export function UnhandledEvents({ rows, locale }: { rows: UnhandledFeedEvents[]; locale: string }) {
  const { t } = useCopy();
  return (
    <Box>
      <SectionNote
        note={t(
          'ai.attention.unhandled.note',
          "Payment events tmm-api doesn't handle yet. They're kept and applied once a build handles them.",
        )}
      />
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('ai.attention.unhandled.aria', 'Unhandled payment events')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.eventType', 'Event type')}</TableCell>
                <TableCell align="right">{t('ai.attention.col.count', 'Count')}</TableCell>
                <TableCell>{t('ai.attention.col.oldest', 'Oldest')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.eventType}>
                  <TableCell>
                    {eventTypeWords(row.eventType, t) ?? (
                      <Box component="code" sx={monoSx}>
                        {row.eventType}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatNumber(row.count, locale)}</TableCell>
                  <TableCell>{formatInstant(row.oldest, locale)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/**
 * 0159 (PAYAPI-0162): pay-api's `sku_review_required` items, as sent. The product is in words (its
 * code when the console has none); pay-api's `detail` is only ever copied (TMM-B3).
 */
export function ProductsToReview({ rows, locale }: { rows: SkuReview[]; locale: string }) {
  const { t } = useCopy();
  const copyDetails = useCopyDetails();
  return (
    <Box>
      <SectionNote
        note={t(
          'ai.attention.skuReview.note',
          "Review the product's tax classification in pay-api's registry.",
        )}
      />
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" aria-label={t('ai.attention.skuReview.aria', 'Products to review')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.product', 'Product')}</TableCell>
                <TableCell>{t('ai.attention.col.since', 'Since')}</TableCell>
                <TableCell>{t('ai.attention.col.details', 'Details')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.sku}>
                  <TableCell>
                    {skuWords(row.sku, t) ?? (
                      <Box component="code" sx={monoSx}>
                        {row.sku}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{formatInstant(row.since, locale)}</TableCell>
                  <TableCell>
                    {row.detail === null || row.detail === '' ? (
                      '—'
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        aria-label={t(
                          'ai.attention.skuReview.copyAria',
                          'Copy details for support for product {{identifier}}',
                          { identifier: row.sku },
                        )}
                        onClick={() => void copyDetails(skuReviewSupportDetails(row))}
                      >
                        {t('ai.attention.copyDetails', 'Copy details for support')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

/**
 * 0159 (PAYAPI-0162): charges pay-api refused `TAX_CLASSIFICATION_REQUIRED`, one row each with its
 * ids as sent. A configuration error: the ids are copied for support, never any server text.
 */
export function TaxClassificationRequired({
  rows,
  locale,
}: {
  rows: TaxClassificationFailure[];
  locale: string;
}) {
  const { t } = useCopy();
  const copyDetails = useCopyDetails();
  return (
    <Box>
      <SectionNote
        note={t(
          'ai.attention.taxClassification.note',
          'A product needs a tax classification in pay-api before it can be charged.',
        )}
      />
      {rows.length === 0 ? (
        <NoneLine />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table
            size="small"
            aria-label={t('ai.attention.taxClassification.aria', 'Tax classification required')}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('ai.attention.col.charge', 'Charge')}</TableCell>
                <TableCell>{t('ai.attention.col.chargeId', 'Charge id')}</TableCell>
                <TableCell>{t('ai.attention.col.tenantId', 'Tenant id')}</TableCell>
                <TableCell>{t('ai.attention.col.resellerId', 'Reseller id')}</TableCell>
                <TableCell>{t('ai.attention.col.failedAt', 'Failed at')}</TableCell>
                <TableCell>{t('ai.attention.col.details', 'Details')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.chargeKind}-${row.chargeId}`}>
                  <TableCell>
                    {chargeKindWords(row.chargeKind, t) ?? (
                      <Box component="code" sx={monoSx}>
                        {row.chargeKind}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <IdCell value={row.chargeId} />
                  </TableCell>
                  <TableCell>
                    <IdCell value={row.tenantId} />
                  </TableCell>
                  <TableCell>
                    <IdCell value={row.resellerId} />
                  </TableCell>
                  <TableCell>{formatInstant(row.failedAt, locale)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      aria-label={t(
                        'ai.attention.taxClassification.copyAria',
                        'Copy details for support for charge {{id}}',
                        { id: row.chargeId },
                      )}
                      onClick={() => void copyDetails(taxFailureSupportDetails(row))}
                    >
                      {t('ai.attention.copyDetails', 'Copy details for support')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
